import { useEffect, useState } from "react";

import { TemplateAdd } from "./TemplateAdd";
import { TemplateConfig } from "./TemplateConfig";
import { TemplateList } from "./TemplateList";
import { TemplatePreview } from "./TemplatePreview";

import styles from "../../../../styles/mensageria.module.scss";

import { OlgaEnvironmentsAPI } from "../../../../providers/OlgaEnvironmentsAPI";
import { Button } from "../../../components/Button";
import { Drawer } from "../../../components/Drawer";
import { Loading } from "../../../components/Loading";
import { If } from "../../../components/control-statements";
import { Environment } from "../../../core/Environment";

const steps = {
  TEMPLATE_LIST: 1,
  TEMPLATE_ADD: 2,
  TEMPLATE_CONFIG: 3,
  TEMPLATE_PREVIEW: 4,
  TEMPLATE_CREATED: 5,
};

const headerTypes = {
  TEXT: "TEXT",
  IMAGE: "IMAGE",
  VIDEO: "VIDEO",
};

export const WhatsAppDrawer = ({ open, onClose, onConfiguredWhatsApp }) => {
  const defaultTemplate = {
    name: null,
    structure: {
      header: null,
      body: {
        text: "",
        examples: [],
      },
      footer: "",
      buttons: [
        {
          type: "URL",
          text: "Acessar o Clube",
          url: Environment?.configurations?.wallet?.configs?.store,
          active: true,
        },
        {
          type: "PHONE_NUMBER",
          text: null,
          ddd: null,
          phone: null,
          url: null,
          active: false,
        },
        {
          type: "PHONE_NUMBER",
          text: null,
          ddd: null,
          phone: null,
          url: null,
          active: false,
        },
      ],
    },
  };

  const [setup, setSetup] = useState({
    header: false,
    footer: false,
    buttons: false,
  });
  const [step, setStep] = useState(steps.TEMPLATE_LIST);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [configuredPhone, setConfiguredPhone] = useState(false);
  const [hasValidSender, setHasValidSender] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [template, setTemplate] = useState(defaultTemplate);
  const [editing, setEditing] = useState(false);

  const resetTemplate = () => {
    setTemplate(defaultTemplate);
  };

  const getSubtitle = () => {
    const titles = {
      1: null,
      2: null,
      3: "Configure o template",
      4: "Prévia do template",
      5: editing
        ? "Template alterado com sucesso"
        : "Template criado com sucesso.",
    };

    return titles[step];
  };

  const showTemplateAdd = () => {
    setSetup({
      header: false,
      footer: false,
      buttons: true,
    });
    setTemplate(defaultTemplate);
    setStep(steps.TEMPLATE_ADD);
  };

  const showTemplateEdit = (template) => {
    const buttons = (template.structure?.buttons || []).map((button) => ({
      ...button,
      active: true,
    }));

    setTemplate({
      ...template,
      structure: {
        ...template.structure,
        buttons: buttons,
      },
    });

    setSetup(template.structure);
    setStep(steps.TEMPLATE_ADD);
    setEditing(true);
  };

  const loadTemplates = async () => {
    setLoading(true);

    const environmentTemplatesReq =
      await OlgaEnvironmentsAPI.getWhatsappTemplates();
    const environmentTemplates = environmentTemplatesReq.getData([]);

    setTemplates(environmentTemplates);
    setLoading(false);
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    const hasTemplates = templates.length > 0;
    if (open && hasTemplates) setStep(steps.TEMPLATE_LIST);
  }, [open, templates]);

  useEffect(() => {
    if (open) {
      const environmentConfigurations = Environment.configurations;
      const { whatsapp } = environmentConfigurations || {};
      const { sender: senderPhone } = whatsapp || {};
      if (senderPhone && senderPhone?.length > 10) {
        setHasValidSender(true);
        const ddd = senderPhone.slice(-11, -9);
        const number = senderPhone.slice(-9);
        setConfiguredPhone({
          ddd,
          number,
        });
      }
    }
  }, [open]);

  const adjustButton = (button) => {
    if (button.type == "PHONE_NUMBER") {
      button.phoneNumber = `55${button.ddd}${button.phone}`;
    }

    return button;
  };

  const saveTemplate = async () => {
    setSaveLoading(true);

    const templateButtons = (template.structure?.buttons || []).filter(
      (button) => button.active
    );
    const newButtons = templateButtons.map((button) => adjustButton(button));

    const newTemplate = {
      ...template,
      structure: {
        ...template.structure,
        buttons: newButtons,
      },
    };

    if (editing) {
      const updateReq = await OlgaEnvironmentsAPI.updateWhatsappTemplate(
        newTemplate.id,
        newTemplate
      );
      const updateErrors = updateReq.getErrors([]);

      if (updateErrors?.length > 0) {
        alert({ text: updateErrors[0] });
        setStep(steps.TEMPLATE_CREATED);
      } else {
        setStep(steps.TEMPLATE_CREATED);
      }
    } else {
      const createReq = await OlgaEnvironmentsAPI.createWhatsappTemplate(
        newTemplate
      );
      const creationErrors = createReq.getErrors([]);

      if (creationErrors?.length > 0) {
        alert({ text: creationErrors[0] });
      } else {
        setStep(steps.TEMPLATE_CREATED);
      }
    }

    setSaveLoading(false);
  };

  const deleteTemplate = async (templateId) => {
    const confirmed = await window.confirm({
      text: "Deseja realmente excluir esse template?",
      confirmText: "Sim, quero excluir",
      cancelText: "Voltar",
    });

    if (confirmed) {
      setLoading(true);

      const updateReq = await OlgaEnvironmentsAPI.deleteWhatsappTemplate(
        templateId
      );
      const updateErrors = updateReq.getErrors([]);

      if (updateErrors?.length > 0) {
        alert({ text: updateErrors[0] });
      } else {
        await loadTemplates();
        setStep(steps.TEMPLATE_LIST);
      }

      setLoading(false);
    }
  };

  const subtitle = getSubtitle();

  return (
    <Drawer
      visible={open}
      onClose={() => onClose()}
      showBackButton={
        ![steps.TEMPLATE_LIST, steps.TEMPLATE_CREATED].includes(step)
      }
      backButton={() => setStep((step) => step - 1)}
      title={step === steps.TEMPLATE_CREATED ? "Sucesso" : "WhatsApp"}
      subtitle={subtitle}
    >
      <If cond={loading}>
        <div className={styles.loading}>
          <Loading />
        </div>
      </If>

      <If cond={!loading && step === steps.TEMPLATE_LIST}>
        <TemplateList
          configuredPhone={configuredPhone}
          newTemplate={() => showTemplateAdd()}
          templates={templates}
          onConfigurePhone={() => {}}
          onEditTemplate={(template) => showTemplateEdit(template)}
          onDeleteTemplate={(id) => deleteTemplate(id)}
        />
      </If>

      <If cond={!loading && step === steps.TEMPLATE_ADD}>
        <TemplateAdd
          setup={setup}
          template={template}
          onChangeSetup={(setup) => setSetup(setup)}
          onChangeTemplate={(data) =>
            setTemplate({
              ...template,
              ...data,
            })
          }
          nextStep={() => setStep(steps.TEMPLATE_CONFIG)}
        />
      </If>

      <If cond={!loading && step === steps.TEMPLATE_CONFIG}>
        <TemplateConfig
          setup={setup}
          template={template}
          onChangeTemplate={(data) =>
            setTemplate({
              ...template,
              ...data,
            })
          }
          nextStep={() => setStep(steps.TEMPLATE_PREVIEW)}
        />
      </If>

      <If cond={!loading && step === steps.TEMPLATE_PREVIEW}>
        <TemplatePreview
          setup={setup}
          template={template}
          loading={saveLoading}
          nextStep={() => saveTemplate()}
        />
      </If>

      <If cond={!loading && step === steps.TEMPLATE_CREATED}>
        <div className={styles.successContainer}>
          <div className={styles.success}>
            <i className="far fa-check" />
          </div>
        </div>

        <div className={styles.footer}>
          <Button
            onClick={() => {
              setEditing(false);
              setStep(steps.TEMPLATE_LIST);
              resetTemplate();
              onConfiguredWhatsApp();
              loadTemplates();
            }}
            className="w-100"
          >
            Fechar
          </Button>
        </div>
      </If>
    </Drawer>
  );
};
